<template>
  <!--
    Full-reading simulator modal. Renders the seeker-facing reading through the
    SHARED <SpreadReading> component — the exact same layout the live reading
    uses — so the Studio preview can't drift from production. The illustration is
    generated on demand (deliberate spend) and shown in SpreadReading's
    illustration slot; three device-viewport toggles reflow the frame.
  -->
  <div class="rm-overlay" @click.self="$emit('close')">
    <div class="rm-shell">
      <header class="rm-head">
        <span class="rm-title">The Reading - as the seeker sees it</span>
        <div class="rm-viewports" role="group" aria-label="Preview viewport">
          <button
            v-for="v in VIEWPORTS" :key="v.key"
            type="button" class="rm-vp" :class="{ active: viewport === v.key }"
            :title="v.label" @click="viewport = v.key"
          >{{ v.glyph }}</button>
        </div>
        <div class="rm-actions">
          <button type="button" class="rm-close" aria-label="Close" @click="$emit('close')">✕</button>
        </div>
      </header>

      <div class="rm-stage">
        <p v-if="error" class="rm-error" role="alert">{{ error }}</p>
        <div v-else-if="loading" class="rm-loading">
          <span class="rm-loading-glyph">✦</span> Weaving the reading…
        </div>
        <SpreadReading
          v-else
          class="rm-spread"
          :question="question"
          :cards="spreadCards"
          :synthesis="output"
          :title="title"
          :viewport="viewport"
        >
          <template #illustration>
            <button
              v-if="imageUrl" type="button" class="rm-illus-thumb"
              title="Click to enlarge" @click="showLightbox = true"
            >
              <img :src="imageUrl" alt="Reading illustration" class="rm-illus-img" />
              <span class="rm-illus-expand" aria-hidden="true">⤢</span>
            </button>
            <template v-else>
              <span v-if="imageLoading" class="rm-illus-loading-glyph">✦</span>
              <button
                v-else type="button" class="rm-illus-gen"
                :disabled="loading || !output" @click="generateIllustration"
              >✨ Generate illustration</button>
              <span v-if="imageLoading" class="rm-illus-loading-label">Painting your vision…</span>
            </template>
          </template>
        </SpreadReading>
      </div>
    </div>

    <!-- Full-size illustration lightbox. -->
    <div v-if="showLightbox && imageUrl" class="rm-lightbox" @click.stop="showLightbox = false">
      <img :src="imageUrl" alt="Reading illustration" class="rm-lightbox-img" />
      <button type="button" class="rm-lightbox-close" aria-label="Close" @click.stop="showLightbox = false">✕</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import SpreadReading from '@/components/ui/games/SpreadReading.vue'
import { runReadingPreview, runImagePreview } from '@/api-service'
import { parseAxiosError } from '@/utils/general-utils'

const props = defineProps({
  skillKey: { type: String, required: true },
  momentIntent: { type: String, default: '' },
  imageDirection: { type: String, default: '' },
  readerSlant: { type: String, default: '' },
  maxWords: { type: Number, default: 0 },
  meaningSource: { type: String, default: 'symbolic' },
  runNonce: { type: Number, default: 0 },
})
defineEmits(['close'])

const VIEWPORTS = [
  { key: 'desktop', label: 'Desktop', glyph: '🖥' },
  { key: 'tablet', label: 'Tablet', glyph: '▭' },
  { key: 'mobile', label: 'Mobile', glyph: '▯' },
]
const viewport = ref('desktop')

const cards = ref([])
const output = ref('')
const title = ref('')
const question = ref('')
const loading = ref(false)
const error = ref('')
// The illustration — generated FROM this reading (deliberate spend, button).
const imageUrl = ref('')
const imageLoading = ref(false)
const showLightbox = ref(false)

// Normalise the preview's card shape into SpreadReading's contract.
const spreadCards = computed(() => (cards.value || []).map(c => ({
  position: c.position,
  name: c.card_name,
  image: c.image_url || '',
  reversed: !!c.reversed,
  meaning: c.meaning || '',
})))

async function generateIllustration() {
  if (imageLoading.value || !output.value) return
  imageLoading.value = true
  imageUrl.value = ''
  try {
    const data = await runImagePreview(props.skillKey, {
      image_direction: props.imageDirection || '',
      reader_slant: props.readerSlant || '',
      meaning_source: props.meaningSource || 'symbolic',
      // Illustrate THIS reading — the exact hand + synthesis shown above.
      cards: cards.value,
      synthesis: output.value,
    })
    imageUrl.value = (data && data.image_url) || ''
  } catch (e) {
    error.value = parseAxiosError(e)?.message || 'Illustration failed. Try again.'
  } finally {
    imageLoading.value = false
  }
}

async function load() {
  if (loading.value) return
  loading.value = true
  error.value = ''
  cards.value = []
  output.value = ''
  title.value = ''
  // A fresh reading invalidates any prior illustration.
  imageUrl.value = ''
  showLightbox.value = false
  try {
    const data = await runReadingPreview(props.skillKey, {
      moment_intent: props.momentIntent || '',
      reader_slant: props.readerSlant || '',
      max_words: props.maxWords || 0,
      meaning_source: props.meaningSource || 'symbolic',
    })
    cards.value = (data && data.cards) || []
    output.value = (data && data.output) || ''
    title.value = (data && data.title) || ''
    question.value = (data && data.question) || ''
  } catch (e) {
    error.value = parseAxiosError(e)?.message || 'Reading preview failed. Try again.'
  } finally {
    loading.value = false
  }
}

watch(() => props.runNonce, load, { immediate: true })
</script>

<style scoped>
.rm-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(8, 4, 20, 0.7);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.rm-shell {
  width: 100%; max-width: 1040px; max-height: 92vh;
  display: flex; flex-direction: column;
  background: #160c2b;
  border: 1px solid rgba(195, 169, 255, 0.22);
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}
.rm-head {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(195, 169, 255, 0.14);
}
.rm-title {
  font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 0.04em;
  color: #C9A94E; flex: 1; min-width: 0;
}
.rm-viewports { display: inline-flex; border: 1px solid rgba(195, 169, 255, 0.28); border-radius: 999px; overflow: hidden; }
.rm-vp {
  background: transparent; border: none; color: rgba(212, 196, 232, 0.7);
  padding: 4px 12px; font-size: 13px; cursor: pointer; transition: all 0.15s;
}
.rm-vp.active { background: rgba(201, 169, 78, 0.18); color: #C9A94E; }
.rm-actions { display: inline-flex; align-items: center; gap: 8px; }
.rm-close {
  background: transparent; border: none; color: rgba(212, 196, 232, 0.7);
  font-size: 16px; cursor: pointer; padding: 2px 6px;
}
.rm-stage {
  flex: 1; min-height: 0; overflow-y: auto;
  display: flex; justify-content: center;
  padding: 22px 16px;
  background:
    radial-gradient(circle at 50% 0%, rgba(201, 169, 78, 0.05), transparent 55%),
    #0e0722;
}
/* SpreadReading carries its own parchment surface — just size it here. */
.rm-spread { align-self: flex-start; width: 100%; }
.rm-error { color: #ff9a9a; margin: 0; }
.rm-loading { color: rgba(212, 196, 232, 0.7); font-style: italic; align-self: center; margin-top: 40px; }
.rm-loading-glyph { animation: rm-pulse 1.2s ease-in-out infinite; }
@keyframes rm-pulse { 50% { opacity: 0.3; } }

/* Generate-illustration button + loading, rendered into SpreadReading's slot. */
.rm-illus-gen {
  background: rgba(102, 51, 153, 0.12);
  border: 1px solid rgba(102, 51, 153, 0.4);
  color: #4e2776;
  border-radius: 999px;
  padding: 8px 16px; font-size: 0.82rem; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.rm-illus-gen:hover:not(:disabled) { background: rgba(102, 51, 153, 0.2); }
.rm-illus-gen:disabled { opacity: 0.5; cursor: default; }
.rm-illus-loading-glyph { font-size: 1.6rem; color: rgba(102, 51, 153, 0.4); animation: rm-pulse 1.2s ease-in-out infinite; }
.rm-illus-loading-label { font-size: 0.8rem; color: #7a6c8c; font-style: italic; }
.rm-illus-thumb {
  position: relative; display: block; padding: 0; border: none; background: none;
  width: 100%; cursor: zoom-in; border-radius: 12px; overflow: hidden;
}
.rm-illus-img { display: block; width: 100%; border-radius: 12px; }
.rm-illus-expand {
  position: absolute; right: 8px; bottom: 8px; width: 28px; height: 28px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(20, 10, 35, 0.6); color: #fff; font-size: 14px; opacity: 0; transition: opacity 0.15s;
}
.rm-illus-thumb:hover .rm-illus-expand { opacity: 1; }
.rm-lightbox {
  position: fixed; inset: 0; z-index: 1100;
  background: rgba(8, 4, 20, 0.85); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; padding: 32px; cursor: zoom-out;
}
.rm-lightbox-img { max-width: 92vw; max-height: 88vh; border-radius: 12px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6); }
.rm-lightbox-close {
  position: absolute; top: 18px; right: 22px;
  background: rgba(0, 0, 0, 0.4); border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff; width: 36px; height: 36px; border-radius: 50%; font-size: 16px; cursor: pointer;
}
</style>
